/**
 * Messages between the chat and the site that shows it in an iframe (the
 * bubble). Plan: docs/plans/2026-10-12-web-chat-refresh, names D3.
 *
 * - `tfc:unread` (chat → site): `{count, active}`. `count` is the number of
 *   staff replies that arrived while the panel was closed; `active` says a
 *   person's reply is being waited for, so the site loads the chat after a
 *   refresh. Never message text: the site is another origin and the payload
 *   is posted to `*`, because the chat does not know which site embeds it.
 * - `tfc:panel` (site → chat): `{open}`. Accepted only from `window.parent`.
 *
 * The site answers every `tfc:unread` with the panel's state, so the chat
 * hears it even when its listener started after the site's iframe `load`.
 */
import { useEffect, useState } from 'react';
import { validateWebsiteVisitId, websiteVisitFromSearch } from './referral';

export function isEmbedded() {
  return typeof window !== 'undefined' && window.parent !== window;
}

export function postUnread({ count, active }) {
  if (!isEmbedded()) {
    return;
  }
  window.parent.postMessage({ type: 'tfc:unread', count, active }, '*');
}

/**
 * Whether the visitor can see the chat. On a page of its own, always. In an
 * iframe, closed until the site says it is open: a reply counts as seen only
 * when the site has confirmed the panel shows it.
 */
export function useHostPanelOpen() {
  const [open, setOpen] = useState(() => !isEmbedded());
  useEffect(() => {
    if (!isEmbedded()) {
      return undefined;
    }
    const onMessage = (event) => {
      if (event.source !== window.parent) {
        return;
      }
      const data = event.data;
      if (!data || data.type !== 'tfc:panel' || typeof data.open !== 'boolean') {
        return;
      }
      setOpen(data.open);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return open;
}

/** Only the known first-party site may update this iframe's optional visit reference. */
export function useWebsiteVisitId() {
  const [visitId, setVisitId] = useState(() => websiteVisitFromSearch(window.location.search));
  useEffect(() => {
    if (!isEmbedded()) return undefined;
    const onMessage = (event) => {
      if (
        event.source !== window.parent ||
        !['https://npas.com.br', 'https://www.npas.com.br'].includes(event.origin) ||
        event.data?.type !== 'tfc:panel' ||
        typeof event.data.open !== 'boolean' ||
        !Object.hasOwn(event.data, 'website_visit_id')
      )
        return;
      try {
        setVisitId(validateWebsiteVisitId(event.data.website_visit_id));
      } catch (error) {
        console.error('[website-visit] Rejected invalid host visit reference', error);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  return visitId;
}
